import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { color, spacing } from '@lightspeed/cirrus-tokens/utils';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';
import some from 'lodash/some';
import pickBy from 'lodash/pickBy';
import { STATUS, PRINTER_STATUSES } from 'pages/views/devices/socketClient/types';
import { getHubEnabled } from 'pages/views/devices/utils/storage';
import { HubState } from 'pages/store/reducers/hub';

type StatusColor = 'orange' | 'green' | 'maple';

const pulse = (statusColor: string) => keyframes`
  0% {
    box-shadow: 0 0 0 0 ${color(statusColor)}40;
  }
  70% {
    box-shadow: 0 0 0 10px ${color(statusColor)}00;
  }
  100% {
    box-shadow: 0 0 0 0 ${color(statusColor)}00;
  }
`;

const StatusIndicator = styled('div')`
  height: ${spacing(2)};
  width: ${spacing(2)};
  border-radius: 100%;
  background-color: ${(props: { statusColor: StatusColor }) => color(props.statusColor)};
  box-shadow: 0 0 0 ${props => color(props.statusColor)}40;
  animation: ${props => pulse(props.statusColor)} 2s infinite;
  transition: all 0.3s ease-in-out;
`;

export const getStatusColor = ({
  connection,
  defaultPrinters,
  selectedDisplay,
}: {
  connection: HubState['connection'];
  defaultPrinters: HubState['defaultPrinters'];
  selectedDisplay: HubState['selectedDisplay'];
}): StatusColor => {
  const isHubConnected = connection.hub === STATUS.CONNECTED;
  const isBrowserCfdConnected = connection.browserCFD === STATUS.CONNECTED;
  const isConnectionError = some(connection, status => status === STATUS.ERROR);
  const hasPrinters = some(defaultPrinters, printer => !isEmpty(printer));
  const isHubCfdConnected = !isEmpty(selectedDisplay);

  const isNoDevicesConnected = !hasPrinters && !isHubCfdConnected && !isBrowserCfdConnected;
  const isEveryPrinterConnected = every(
    pickBy(defaultPrinters),
    printer =>
      printer?.status === PRINTER_STATUSES.READY ||
      printer?.status === PRINTER_STATUSES.NOT_AVAILABLE
  );

  if ((!isHubConnected && !isBrowserCfdConnected) || isConnectionError || isNoDevicesConnected) {
    return 'maple';
  }

  if (hasPrinters && !isEveryPrinterConnected) {
    return 'orange';
  }

  return 'green';
};

const Indicator: React.FC = () => {
  const isHubEnabled = getHubEnabled();
  const connection = useSelector(state => state.hub.connection);
  const defaultPrinters = useSelector(state => state.hub.defaultPrinters);
  const selectedDisplay = useSelector(state => state.hub.selectedDisplay);

  return isHubEnabled ? (
    <StatusIndicator
      data-test="status-indicator"
      statusColor={getStatusColor({ connection, defaultPrinters, selectedDisplay })}
    />
  ) : null;
};

export default Indicator;
